import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { DashboardLayout } from '../components/DashboardLayout';
import ProjectDashboardCharts from '../components/ProjectDashboardCharts';
import IdsExportImportButtons from '../components/IdsExportImportButtons';
import ApplicabilityChip from '../components/ApplicabilityChip';
import HitlReviewQueue from '../components/HitlReviewQueue';
import ReExtractWarningDialog from '../components/ReExtractWarningDialog';
import UnconfirmedCandidatesModal from '../components/UnconfirmedCandidatesModal';
import BEPReadinessPanel from '../components/BEPReadinessPanel';
import { BepExtractionHologram } from '../components/BepExtractionHologram';
import { ModelBadge } from '../components/ValidationKpiDashboard';
import { deriveConfirmRulesOutcome } from '../lib/confirmRulesOutcome';
import {
  reviewIdentity,
  canHumanSelect,
  mergeReviewCandidates,
  buildReviewSubmission,
} from '../lib/reviewCandidates';
import { ReviewRuntimeStatus } from '../components/ReviewRuntimeStatus';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Building2, FileText, FileCode, BookOpen, Upload, X, Clock,
  CheckCircle2, Loader2, ArrowLeft, Shield, Users, Layers, Calendar,
  AlertCircle, Check, SquareCheck, Square, ListChecks, Trash2, Plus, Download, XCircle, Sparkles,
  ChevronDown, ChevronRight, LockKeyhole,
} from 'lucide-react';
import { apiFetch } from '../lib/supabase';
import { saveBlobWithPicker, openManualDownload } from '../lib/fileSave';
import {
  VALIDATION_PROFILE_OPTIONS,
  VALIDATION_TONE_CLASSES,
} from '../lib/validationProfileOptions';
import { toast } from 'sonner';
import { useTimezone } from '../contexts/TimezoneContext';
import { selectProjectValidationHistory, unavailableIfcLabel } from '../lib/projectAuditHistory';
import { uploadFileInChunks } from '../lib/chunkUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function maskEmail(email) {
  if (!email) return '***@***.***';
  const [user, domain] = email.split('@');
  const maskedUser = user.slice(0, 2) + '***' + (user.length > 3 ? user.slice(-1) : '');
  const domainParts = domain.split('.');
  const maskedDomain = domainParts[0].slice(0, 2) + '***.' + domainParts.slice(1).join('.');
  return `${maskedUser}@${maskedDomain}`;
}

function RuleSaveConfirmation({ summary }) {
  if (!summary) return null;
  return (
    <Alert className="border-[hsl(var(--success))]/35 bg-[hsl(var(--success))]/5"
      data-testid="rule-save-confirmation">
      <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />
      <AlertDescription className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <strong data-testid="rule-save-confirmation-title">Requirements saved</strong>
        <span data-testid="rule-save-count">{summary.saved} saved</span>
        <span data-testid="rule-active-count">{summary.active} active</span>
        <span data-testid="rule-inactive-count">{summary.inactive} inactive</span>
        <span data-testid="rule-engine-pending-count">{summary.enginePending} engine pending</span>
        <span data-testid="rule-scope-count">{summary.requiresScope} require scope</span>
        <span data-testid="rule-blocked-count">{summary.blocked} blocked</span>
      </AlertDescription>
    </Alert>
  );
}

const SEVERITY_COLORS = {
  critical: 'bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30',
  major: 'bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
  minor: 'bg-accent/10 text-accent border-accent/30',
};

function MiniDropZone({ label, icon: Icon, accept, files, onDrop, onRemove, testId, multiple = false,
  showPreviousPicker = false, previousUploads = [], selectedPrevious = null, onSelectPrevious }) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept, multiple });
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-accent" />
        {showPreviousPicker ? (
          <button type="button"
            onClick={() => setPickerOpen(o => !o)}
            className="text-sm font-medium text-foreground hover:text-accent transition-colors flex items-center gap-1"
            data-testid={`${testId}-previous-toggle`}>
            {label}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${pickerOpen ? 'rotate-180' : ''}`} />
          </button>
        ) : (
          <span className="text-sm font-medium text-foreground">{label}</span>
        )}
      </div>
      {showPreviousPicker && pickerOpen && (
        <div className="border border-border rounded-md bg-popover max-h-56 overflow-y-auto"
          data-testid={`${testId}-previous-list`}>
          {previousUploads.length === 0 && (
            <p className="p-3 text-xs text-muted-foreground">No previously uploaded IFC files for this project yet.</p>
          )}
          {previousUploads.map((u) => (
            <button key={u.ifc_file_hash} type="button"
              disabled={!u.available}
              onClick={() => { onSelectPrevious(u); setPickerOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs border-b border-border last:border-b-0 flex items-center justify-between gap-2 ${
                u.available ? 'hover:bg-muted/60 cursor-pointer' : 'opacity-50 cursor-not-allowed'
              } ${selectedPrevious?.ifc_file_hash === u.ifc_file_hash ? 'bg-accent/10' : ''}`}
              data-testid={`${testId}-previous-item-${u.audit_id}`}>
              <span className="truncate flex-1">{u.ifc_file_name}</span>
              <span className="text-muted-foreground shrink-0 ml-2"
                data-testid={`${testId}-previous-status-${u.audit_id}`}>
                {u.available
                  ? new Date(u.created_at).toLocaleDateString()
                  : unavailableIfcLabel(u.availability_status)}
              </span>
            </button>
          ))}
        </div>
      )}
      {selectedPrevious && (
        <div className="flex items-center justify-between p-2 bg-accent/10 border border-accent/30 rounded text-xs"
          data-testid="selected-previous-upload">
          <span className="truncate flex-1">Reusing: {selectedPrevious.ifc_file_name}</span>
          <button onClick={() => onSelectPrevious(null)} className="p-0.5 hover:bg-destructive/10 rounded">
            <X className="h-3 w-3 text-muted-foreground" />
          </button>
        </div>
      )}
      <div {...getRootProps()} data-testid={testId}
        className={`border border-dashed rounded-lg p-4 text-center cursor-pointer transition-all text-xs
          ${isDragActive ? 'border-accent bg-accent/10' : 'border-border hover:border-accent/50 hover:bg-muted/30'}`}>
        <input {...getInputProps()} />
        <Upload className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
        <p className="text-muted-foreground">Drop file here</p>
      </div>
      {files.map((f) => (
        <div key={f.id} className="flex items-center justify-between p-2 bg-muted/60 rounded text-xs">
          <span className="truncate flex-1">{f.name}</span>
          <span className="text-muted-foreground mx-2">
            {f.size < 1048576 ? `${(f.size / 1024).toFixed(1)} KB` : `${(f.size / 1024 / 1024).toFixed(1)} MB`}
          </span>
          <button onClick={() => onRemove(f.id)} className="p-0.5 hover:bg-destructive/10 rounded">
            <X className="h-3 w-3 text-muted-foreground" />
          </button>
        </div>
      ))}
    </div>
  );
}

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { formatDateTime, formatDuration } = useTimezone();
  const [project, setProject] = useState(null);
  const [dbProject, setDbProject] = useState(null);
  const isReadOnly = dbProject ? Boolean(dbProject.is_read_only) : true;

  // State
  const [step, setStep] = useState(1); // 1 = BEP rules review, 2 = IFC validation

  // Deep link from the readiness dashboard: switch to step 1 + scroll.
  useEffect(() => {
    if (window.location.hash === '#bep-step1-anchor') {
      setStep(1);
      const t = setTimeout(() =>
        document.getElementById('bep-step1-anchor')?.scrollIntoView({ behavior: 'smooth' }), 800);
      return () => clearTimeout(t);
    }
  }, []);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasConfirmedRules, setHasConfirmedRules] = useState(false);

  // Step 1 state
  const [bepFiles, setBepFiles] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [extractStep, setExtractStep] = useState('');
  const [candidateRules, setCandidateRules] = useState([]);
  // P02 Batch 2 — gated (non-selectable) groups collapse state + per-rule
  // blocked outcomes returned by the confirm endpoint.
  const [expandedGateGroups, setExpandedGateGroups] = useState({});
  const [confirmBlocked, setConfirmBlocked] = useState([]);
  const [ruleSaveSummary, setRuleSaveSummary] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [bepDuplicate, setBepDuplicate] = useState(null);
  const [showReExtract, setShowReExtract] = useState(false);
  const [hitlPending, setHitlPending] = useState(0);
  // Job ID kept in state so a "still running past the poll cap"
  // scenario doesn't lose the reference — page refresh can pick it
  // up and resume polling instead of the user re-uploading.
  const [bepJobId, setBepJobId] = useState(null);

  // Step 2 state
  const [ifcFiles, setIfcFiles] = useState([]);
  const [omFiles, setOmFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStep, setUploadStep] = useState('');

  // Real BEP from bep_registry (replaces hardcoded mock for non-WeZig projects)
  const [realBep, setRealBep] = useState(null);
  const [hasEligibleCurrent, setHasEligibleCurrent] = useState(true);
  const [bepLoading, setBepLoading] = useState(true);
  const [bepError, setBepError] = useState('');
  // Modal: view rules extracted from BEP (stored in bep_registry.candidate_rules)
  const [showBepRules, setShowBepRules] = useState(false);
  const [bepRulesFilter, setBepRulesFilter] = useState('');

  // Step 2 — Confirmed Rules management
  const [confirmedRules, setConfirmedRules] = useState([]);
  const [rulesCounts, setRulesCounts] = useState({ active: 0, inactive: 0 });
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesExpanded, setRulesExpanded] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [ruleSearch, setRuleSearch] = useState('');
  const [addingRule, setAddingRule] = useState(false);
  const [newRuleDesc, setNewRuleDesc] = useState('');
  const [newRuleCategory, setNewRuleCategory] = useState('GENERAL');
  const [newRuleSeverity, setNewRuleSeverity] = useState('major');

  // Step 2 — Validation Profile (sent to backend with the audit run)
  // Default per spec: custom_bep_rules only
  const [validationProfile, setValidationProfile] = useState({
    naming_compliance: false,
    ifc_schema: false,
    fm_parameters: false,
    geometry_completeness: false,
    cobie_handover: false,
    om_matching: false,
    custom_bep_rules: true,
  });

  // Duplicate-audit dialog state. Populated when the upload endpoint
  // returns `already_analyzed: true` (same IFC hash + same profile already
  // ran successfully). Lets the user View existing or Run anyway.
  const [duplicateAuditInfo, setDuplicateAuditInfo] = useState(null);

  const toggleProfileOption = (key) => {
    setValidationProfile(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Unconfirmed candidate rules (BEP candidates not yet activated) ──
  const [unconfirmedCount, setUnconfirmedCount] = useState(0);
  const [unconfirmedModalOpen, setUnconfirmedModalOpen] = useState(false);
  const fetchUnconfirmedCount = useCallback(async () => {
    if (!projectId) return;
    try {
      const resp = await apiFetch(
        `${API_URL}/api/projects/${projectId}/bep/unconfirmed-candidates`
      );
      if (!resp.ok) return;
      const json = await resp.json();
      setUnconfirmedCount(json.unconfirmed_count || 0);
    } catch (err) {
      console.warn('fetchUnconfirmedCount failed:', err);
    }
  }, [projectId]);

  const fetchConfirmedRules = useCallback(async () => {
    if (!projectId) return;
    setRulesLoading(true);
    let loaded = null;
    try {
      const resp = await apiFetch(`${API_URL}/api/projects/${projectId}/rules?include_inactive=true`);
      if (resp.ok) {
        const data = await resp.json();
        loaded = data;
        setConfirmedRules(data.rules || []);
        setRulesCounts({ active: data.active_count || 0, inactive: data.inactive_count || 0 });
      }
    } catch (err) { console.warn('fetchConfirmedRules failed:', err); }
    finally { setRulesLoading(false); }
    // Refresh the unconfirmed-candidates count whenever the active rules
    // list refreshes — they share a refresh cycle so the
    // "+N available rules" button reflects the latest server state
    // without a separate polling loop.
    fetchUnconfirmedCount();
    return loaded;
  }, [projectId, fetchUnconfirmedCount]);
  // Initial fetch + on projectId change.
  useEffect(() => { fetchUnconfirmedCount(); }, [fetchUnconfirmedCount]);

  // Refresh the "BEP exists" state after extraction so the readiness
  // panel enables without a page reload.
  const refreshBep = useCallback(async () => {
    try {
      const resp = await apiFetch(`${API_URL}/api/projects/${projectId}/bep`);
      if (!resp.ok) return;
      const d = await resp.json();
      setHasEligibleCurrent(d.has_eligible_current !== false);
      if (d.has_bep) {
        setRealBep({
          ...d.bep,
          project_name: d.project_name,
          project_code: d.project_code,
          project_originator: d.project_originator,
          project_created_at: d.project_created_at,
        });
      }
    } catch { /* non-fatal */ }
  }, [projectId]);

  // Refresh confirmed rules whenever we land on Step 2
  useEffect(() => {
    if (step === 2) fetchConfirmedRules();
  }, [step, fetchConfirmedRules]);

  const toggleConfirmedRule = async (rule) => {
    const newActive = !rule.is_active;
    // Phase 1 traceability — collect an optional deactivation reason.
    let reason = '';
    if (!newActive) {
      reason = window.prompt('Optional: reason for deactivating this rule (leave blank to skip)', '') || '';
    }
    // Optimistic update
    setConfirmedRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_active: newActive } : r));
    setRulesCounts(prev => ({
      active: prev.active + (newActive ? 1 : -1),
      inactive: prev.inactive + (newActive ? -1 : 1),
    }));
    try {
      const resp = await apiFetch(`${API_URL}/api/projects/${projectId}/rules/${rule.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: newActive, reason }),
      });
      if (!resp.ok) throw new Error('Toggle failed');
    } catch (err) {
      // Revert on failure
      console.warn('toggleConfirmedRule failed:', err);
      setConfirmedRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_active: !newActive } : r));
      setRulesCounts(prev => ({
        active: prev.active + (newActive ? -1 : 1),
        inactive: prev.inactive + (newActive ? 1 : -1),
      }));
    }
  };

  const deleteConfirmedRule = async (rule) => {
    if (!window.confirm(`Delete this rule permanently?\n\n"${rule.description}"`)) return;
    try {
      const resp = await apiFetch(`${API_URL}/api/projects/${projectId}/rules/${rule.id}`, { method: 'DELETE' });
      if (resp.ok) {
        setConfirmedRules(prev => prev.filter(r => r.id !== rule.id));
        setRulesCounts(prev => ({
          active: rule.is_active ? prev.active - 1 : prev.active,
          inactive: !rule.is_active ? prev.inactive - 1 : prev.inactive,
        }));
      }
    } catch (err) { console.warn('deleteConfirmedRule failed:', err); }
  };

  const addManualRule = async () => {
    if (!newRuleDesc.trim()) return;
    setAddingRule(true);
    try {
      const resp = await apiFetch(`${API_URL}/api/projects/${projectId}/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: newRuleDesc.trim(),
          category: newRuleCategory,
          severity: newRuleSeverity,
        }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setConfirmedRules(prev => [...prev, data.created]);
        setRulesCounts(prev => ({ ...prev, active: prev.active + 1 }));
        setNewRuleDesc('');
        setNewRuleSeverity('major');
        setNewRuleCategory('GENERAL');
      }
    } catch (err) { console.warn('addManualRule failed:', err); }
    finally { setAddingRule(false); }
  };

  const makeFileObj = (f) => ({ file: f, id: Math.random().toString(36).substring(7), name: f.name, size: f.size });

  const fetchData = useCallback(async () => {
    // None of these five depend on each other's result — fire them all at
    // once instead of awaiting one at a time, then process each outcome
    // with the exact same try/catch/error-state behavior as before so one
    // endpoint failing never blocks or clears another's already-loaded state.
    const [auditsSettled, rulesSettled, bepSettled, projectSettled, hitlSettled] = await Promise.allSettled([
      apiFetch(`${API_URL}/api/audits?project_id=${projectId}`),
      apiFetch(`${API_URL}/api/projects/${projectId}/rules`),
      apiFetch(`${API_URL}/api/projects/${projectId}/bep`),
      apiFetch(`${API_URL}/api/projects/${projectId}`),
      apiFetch(`${API_URL}/api/projects/${projectId}/rules/hitl`),
    ]);

    try {
      let auditList = [];
      if (auditsSettled.status === 'rejected') throw auditsSettled.reason;
      const auditsResp = auditsSettled.value;
      if (auditsResp.ok) {
        const json = await auditsResp.json();
        auditList = selectProjectValidationHistory(json.audits || []);
        setAudits(auditList);
      }
      // Check if project already has confirmed rules
      if (rulesSettled.status === 'rejected') throw rulesSettled.reason;
      const rulesResp = rulesSettled.value;
      let alreadyConfirmed = false;
      if (rulesResp.ok) {
        const rulesData = await rulesResp.json();
        if (rulesData.total > 0) {
          alreadyConfirmed = true;
          setHasConfirmedRules(true);
          setStep(2);
          setManifest(rulesData.manifest);
        }
      }
      // If audits exist, allow skipping to Step 2 even without explicit rule confirmation
      if (!alreadyConfirmed && auditList.length > 0) {
        setHasConfirmedRules(true);
      }
    } catch (err) {
      console.error('fetchData failed:', err);
      setError('Unable to load project data');
    }

    // Fetch real BEP from bep_registry (overrides hardcoded mock if found)
    setBepLoading(true);
    setBepError('');
    try {
      if (bepSettled.status === 'rejected') throw bepSettled.reason;
      const bepResp = bepSettled.value;
      if (!bepResp.ok) {
        if (bepResp.status === 404) {
          setRealBep(null); // project doesn't exist or has no BEP — render hidden
        } else {
          setBepError(`Failed to load BEP (${bepResp.status})`);
        }
      } else {
        const bepData = await bepResp.json();
        setHasEligibleCurrent(bepData.has_eligible_current !== false);
        if (bepData.has_bep) {
          setRealBep({
            ...bepData.bep,
            project_name: bepData.project_name,
            project_code: bepData.project_code,
            project_originator: bepData.project_originator,
            project_created_at: bepData.project_created_at,
          });
          // Hydrate the review panel from the durable Supabase copy
          // so it survives polling timeouts and page reloads.
          if (bepData.bep.candidate_rules && bepData.bep.candidate_rules.length > 0) {
            // Mark accepted rules as selected by default; gated
            // (non-selectable) rows can never be selected.
            setCandidateRules(current => mergeReviewCandidates(
              current, bepData.bep.candidate_rules
            ));
            // Also populate the manifest so the confirm submission has it
            if (bepData.bep.manifest) {
              setManifest(bepData.bep.manifest);
            }
          }
        } else {
          setRealBep(null);
          // For DB projects (not the hardcoded WeZig fixture), build minimal project info
          if (bepData.project_name) {
            setDbProject({
              id: bepData.project_uuid,
              name: bepData.project_name,
              code: bepData.project_code,
              originator: bepData.project_originator || '',
              created_at: bepData.project_created_at,
            });
            setProject({
              name: bepData.project_name,
              code: bepData.project_code,
              originator: bepData.project_originator || '',
              bep: null,
            });
          }
        }
      }
    } catch (err) {
      console.warn('Failed to fetch project BEP:', err);
      setBepError('Network error while loading BEP details');
    } finally {
      setBepLoading(false);
    }
    // Backfill project info from /projects if not already loaded
    try {
      if (projectSettled.status === 'rejected') throw projectSettled.reason;
      const pResp = projectSettled.value;
      if (pResp.ok) {
        const pData = await pResp.json();
        setDbProject(pData);
        setProject((current) => current && current.name ? current : {
          name: pData.name,
          code: pData.code,
          originator: pData.originator || '',
          bep: null,
        });
      }
    } catch (err) {
      console.warn('Failed to fetch DB project:', err);
    }
    // Pending HITL count — drives the amber indicator on the Step 1 tab.
    try {
      if (hitlSettled.status === 'rejected') throw hitlSettled.reason;
      const hResp = hitlSettled.value;
      if (hResp.ok) {
        const hData = await hResp.json();
        setHitlPending(hData.total || 0);
      }
    } catch (err) {
      console.warn('Failed to fetch HITL count:', err);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const lastReviewDate = audits.length > 0
    ? new Date(audits.sort((a, b) => new Date(b.completed_at || b.created_at) - new Date(a.completed_at || a.created_at))[0].completed_at || audits[0].created_at)
    : null;

  // ── Step 1: Extract rules from BEP (async with polling) ──
  const extractRules = async ({ force = false } = {}) => {
    if (bepFiles.length === 0) { setError('Upload a BEP file first'); return; }
    setExtracting(true);
    setError('');
    setExtractStep(force ? 'Forcing re-extraction...' : 'Uploading BEP...');

    try {
      const formData = new FormData();
      formData.append('bep_file', bepFiles[0].file);

      const url = `${API_URL}/api/projects/${projectId}/upload-bep${force ? '?force=true' : ''}`;
      const resp = await apiFetch(url, {
        method: 'POST', body: formData,
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }

      const data = await resp.json();
      
      // Check if BEP was already analyzed
      if (data.already_analyzed) {
        setBepDuplicate(data);
        setExtractStep('');
        return;
      }
      
      // Async extraction — poll for status
      if (data.job_id) {
        setBepDuplicate(null);
        setExtractStep('AI is extracting rules (this takes 2-3 minutes)...');

        // Poll every 5s. Cap is generous enough to cover the
        // Terra/Sol pipeline (~15-20 min on a 260-page BEP) with
        // room for OpenAI rate-limit retries. GPT-4o legacy on the
        // same PDF is ~4 min; the shared upper bound is fine.
        // 300 * 5s = 25 minutes.
        const POLL_INTERVAL_MS = 5000;
        const POLL_MAX_ATTEMPTS = 300;
        let attempts = 0;
        while (attempts < POLL_MAX_ATTEMPTS) {
          await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
          attempts++;

          try {
            const statusResp = await apiFetch(`${API_URL}/api/projects/${projectId}/bep-status/${data.job_id}`);
            if (!statusResp.ok) continue;
            const status = await statusResp.json();

            setExtractStep(status.step || 'Processing...');

            if (status.status === 'completed' || status.status === 'completed_with_warnings') {
              // P02 Batch 2 — hydrate from the DB-derived serving path
              // (gated candidates), not the job file's legacy in-memory list.
              let hydrated = false;
              try {
                const bepResp = await apiFetch(`${API_URL}/api/projects/${projectId}/bep`);
                if (bepResp.ok) {
                  const bepData = await bepResp.json();
                  if (bepData.has_bep && (bepData.bep.candidate_rules || []).length > 0) {
                    setCandidateRules(current => mergeReviewCandidates(
                      current, bepData.bep.candidate_rules
                    ));
                    if (bepData.bep.manifest) setManifest(bepData.bep.manifest);
                    hydrated = true;
                  }
                }
              } catch { /* fall back to the job payload below */ }
              if (!hydrated) {
                setCandidateRules(current => mergeReviewCandidates(
                  current, status.candidate_rules || []
                ));
                setManifest(status.manifest);
              }
              refreshBep();
              if (status.status === 'completed_with_warnings') {
                setExtractStep(`Extraction completed with warnings: ${status.chunks_failed || 0} of ${status.chunks_total || 0} chunks had issues.`);
              } else {
                setExtractStep('');
              }
              return;
            }
            if (status.status === 'failed') {
              throw new Error(status.error || 'Extraction failed');
            }
          } catch (pollErr) {
            // Surface any genuine backend error regardless of the
            // exact wording. Transient network blips (fetch failure,
            // 5xx) leave ``pollErr`` without a message we can key on
            // — keep polling only when the error is clearly
            // transient. Anything else propagates so the user isn't
            // left staring at a stuck "processing" spinner.
            const msg = String(pollErr && pollErr.message || '');
            const isTransient = (
              msg === '' ||
              msg.includes('NetworkError') ||
              msg.includes('Failed to fetch') ||
              msg.includes('AbortError') ||
              /5\d\d/.test(msg) // 5xx
            );
            if (!isTransient) throw pollErr;
            // Otherwise swallow and continue polling.
          }
        }
        // Polling cap reached. The backend thread is still running —
        // do NOT declare a hard timeout. Check the DB once (in case
        // the run finished between polls), and if it hasn't, keep
        // the job_id in state and prompt the user to check back.
        try {
          const bepResp = await apiFetch(`${API_URL}/api/projects/${projectId}/bep`);
          if (bepResp.ok) {
            const bepData = await bepResp.json();
            if (bepData.has_bep && bepData.bep.candidate_rules && bepData.bep.candidate_rules.length > 0) {
              // Extraction did succeed — hydrate from Supabase and
              // proceed without throwing.
              setCandidateRules(current => mergeReviewCandidates(
                current, bepData.bep.candidate_rules
              ));
              if (bepData.bep.manifest) setManifest(bepData.bep.manifest);
              refreshBep();
              setExtractStep('');
              return;
            }
          }
        } catch (finalCheckErr) {
          // Fall through to the "still running" notice below.
        }
        // Still running on the backend — inform the user without
        // trashing the job. Keep ``bepJobId`` in state so a page
        // refresh (or a later click on "check status") can resume.
        setBepJobId(data.job_id);
        setExtractStep(
          `Extraction still running on the server (job ${data.job_id.slice(0, 8)}…). ` +
          `The Terra/Sol pipeline can take up to 20 minutes on large BEPs — ` +
          `check back in a few minutes, or refresh this page to resume polling.`
        );
        return;
      }
      
      // Fallback: synchronous response (small BEP)
      if (data.candidate_rules) {
        setBepDuplicate(null);
        setCandidateRules(current => mergeReviewCandidates(
          current, data.candidate_rules || []
        ));
        setManifest(data.manifest);
        refreshBep();
        setExtractStep('');
      }
    } catch (err) {
      setError(err.message);
      setExtractStep('');
    } finally {
      setExtracting(false);
    }
  };

  const toggleRule = (id) => {
    setCandidateRules(prev => prev.map(r =>
      reviewIdentity(r) === id ? (!canHumanSelect(r) ? r : { ...r, selected: !r.selected }) : r));
  };
  const setReviewScope = (id, classes) => setCandidateRules(prev => prev.map(r =>
    reviewIdentity(r) === id ? { ...r, target_ifc_classes: classes } : r));

  // Phase 1 traceability — explicit review status for unselected candidates.
  const setCandidateDecision = (id, status) => {
    setCandidateRules(prev => prev.map(r => reviewIdentity(r) === id ? { ...r, review_status: status } : r));
  };

  const selectAll = () => setCandidateRules(prev => prev.map(r =>
    (!canHumanSelect(r) ? r : { ...r, selected: true })));
  const deselectAll = () => setCandidateRules(prev => prev.map(r => ({ ...r, selected: false })));

  // P02 Batch 2 — split the review list: accepted candidates render
  // expanded/selectable; gated ones collapse under per-status groups.
  const selectableRules = candidateRules.filter(r => r.selectable !== false);
  const gatedRules = candidateRules.filter(r => r.selectable === false);
  const humanRules = candidateRules.filter(canHumanSelect);
  const GATE_GROUPS = [
    ['needs_review', 'Needs BIM Review'],
    ['awaiting_engine', 'Awaiting engine features'],
    ['hitl_routed', 'HITL review'],
    ['classified_invalid', 'Invalid'],
    ['not_machine_checkable', 'Not machine-checkable'],
  ];

  // Shared blob-save handler — gives the user a real native "Save as…"
  // dialog inside the Emergent preview iframe via the save.html
  // handoff page (see lib/fileSave.js for the full contract).
  //
  // The previous implementation built an in-page `<a download>` and
  // clicked it programmatically — which works top-frame but is
  // silently blocked inside the iframe. Symptom: the user clicked
  // "Active Rules (PDF)" / "AI Rules Analysis (PDF)" / "IDS Export"
  // and nothing visible happened. This helper routes through the
  // 3-tier saveBlobWithPicker flow so:
  //   • top-frame Chromium → real Save As dialog,
  //   • top-frame Safari/Firefox → silent Downloads folder,
  //   • inside the iframe → sticky toast with a "Save report" button
  //     that opens /save.html in a new tab and posts the blob there,
  //     where showSaveFilePicker finally works.
  const _saveBlobWithToast = async (blob, filename, mimeType, errPrefix) => {
    const outcome = await saveBlobWithPicker(blob, filename, mimeType);
    if (outcome.cancelled) return;
    if (outcome.manual_action) {
      const sizeKb = `${(blob.size / 1024).toFixed(1)} KB`;
      toast.message(`${filename} is ready`, {
        description: `${sizeKb} · Click "Save report" to pick where to save it on your computer.`,
        duration: 30000,
        action: {
          label: 'Save report',
          onClick: () => {
            const opened = openManualDownload(outcome, (progress) => {
              if (progress.type === 'aha-save-complete') {
                toast.success(`Saved · ${progress.filename}`, { duration: 5000 });
              } else if (progress.type === 'aha-save-cancelled') {
                toast.message('Save cancelled', { duration: 4000 });
              } else if (progress.type === 'aha-save-error') {
                toast.error('Could not save the file', {
                  description: progress.error || 'Unknown error',
                  duration: 10000,
                });
              } else if (progress.type === 'popup-blocked') {
                toast.error('Pop-up was blocked', {
                  description:
                    'Allow pop-ups for this site so AHA can open the Save dialog. The file opened in a preview tab as a fallback — use Ctrl/Cmd+S there.',
                  duration: 12000,
                });
              }
            });
            if (!opened) {
              toast.error('Could not open the Save dialog', {
                description:
                  'Allow pop-ups for this site, or open AHA in its own browser tab (outside the preview).',
                duration: 12000,
              });
            }
          },
        },
      });
      return;
    }
    const successMsg =
      outcome.location === 'picked'
        ? `Saved to your chosen location · ${outcome.filename}`
        : `Saved to your Downloads folder · ${outcome.filename}`;
    toast.success(successMsg, {
      description: `${(blob.size / 1024).toFixed(1)} KB`,
      duration: 6000,
    });
    // errPrefix is unused on the success path but kept in the signature so
    // every caller passes the user-facing label and we can re-introduce it
    // if we ever want a per-callsite success caption.
    void errPrefix;
  };

  // Download confirmed rules in the requested format. apiFetch attaches the
  // Supabase JWT; we hand the blob to _saveBlobWithToast so the iframe-safe
  // save-handoff flow runs.
  const exportRules = async (fmt) => {
    const mimeTypeByFmt = {
      pdf: 'application/pdf',
      csv: 'text/csv',
      json: 'application/json',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    };
    const tLoad = toast.loading(`Preparing active rules ${fmt.toUpperCase()}…`);
    try {
      const resp = await apiFetch(
        `${API_URL}/api/projects/${projectId}/rules/export?format=${fmt}`,
        { headers: { Accept: '*/*' } }
      );
      if (!resp.ok) {
        const t = await resp.text().catch(() => '');
        throw new Error(`Export failed (${resp.status}): ${t.slice(0, 160)}`);
      }
      const blob = await resp.blob();
      // Derive a filename from Content-Disposition, fall back to a sensible default.
      const cd = resp.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `bep_rules.${fmt}`;
      toast.dismiss(tLoad);
      await _saveBlobWithToast(blob, filename, mimeTypeByFmt[fmt], 'Active rules export');
    } catch (e) {
      toast.dismiss(tLoad);
      toast.error('Active rules export failed', {
        description: e.message || 'Unknown error',
        duration: 8000,
      });
      setError(e.message || 'Export failed');
    }
  };

  // AI-generated strategic analysis of the project's active BEP rules.
  // Streams a PDF. Same look as the audit Remediation Report — exec
  // summary + per-category coverage + recommended additions + risk flags.
  const [aiReportLoading, setAiReportLoading] = useState(false);
  const downloadAiRulesReport = async () => {
    setAiReportLoading(true);
    setError('');
    const tLoad = toast.loading('Generating AI Rules Analysis…');
    try {
      const resp = await apiFetch(
        `${API_URL}/api/projects/${projectId}/rules/ai-report`,
        { headers: { Accept: 'application/pdf' } }
      );
      if (!resp.ok) {
        const t = await resp.text().catch(() => '');
        throw new Error(`AI Rules Analysis failed (${resp.status}): ${t.slice(0, 200)}`);
      }
      const blob = await resp.blob();
      const cd = resp.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `AHA_BEP_rules_analysis.pdf`;
      toast.dismiss(tLoad);
      await _saveBlobWithToast(blob, filename, 'application/pdf', 'AI Rules Analysis');
    } catch (e) {
      toast.dismiss(tLoad);
      toast.error('AI Rules Analysis failed', {
        description: e.message || 'Unknown error',
        duration: 8000,
      });
      setError(e.message || 'AI Rules Analysis failed');
    } finally {
      setAiReportLoading(false);
    }
  };


  const confirmRules = async () => {
    const { confirmedRules: selected, decisions } = buildReviewSubmission(candidateRules);
    if (selected.length === 0) { setError('Select at least one rule'); return; }
    setConfirming(true);
    setError('');

    try {
      const resp = await apiFetch(`${API_URL}/api/projects/${projectId}/confirm-rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bep_file_name: bepFiles[0]?.name || 'Unknown',
          manifest: { ...manifest, project_name: project?.name },
          confirmed_rules: selected,
          decisions,
        }),
      });

      if (!resp.ok) throw new Error('Failed to confirm rules');

      // P02 Batch 2 (F3) — surface per-rule outcomes. Blocked rules show
      // the verbatim promotion-guard reason instead of vanishing.
      const data = await resp.json().catch(() => ({}));
      const outcome = deriveConfirmRulesOutcome(data);
      setCandidateRules(prev => prev.map(rule => {
        const result = (data.results || []).find(item => rule.candidate_pk
          ? item.candidate_pk === rule.candidate_pk : item.rule_id === rule.id);
        if (!result) return rule;
        return { ...rule, review_outcome: result,
          human_decision: result.status === 'activated' ? 'confirmed' : result.status === 'selected_engine_pending' ? 'deferred' : rule.human_decision,
          confirmed_rule_id: result.confirmed_rule_id || rule.confirmed_rule_id,
          user_selected: result.status === 'activated' || result.status === 'selected_engine_pending' };
      }));
      setConfirmBlocked(outcome.blocked);
      const refreshed = await fetchConfirmedRules();
      const active = Number(refreshed?.active_count ?? data.active_rules ?? outcome.activeCount);
      const inactive = Number(refreshed?.inactive_count ?? data.inactive_rules ?? 0);
      const summary = {
        saved: outcome.savedCount,
        active,
        inactive,
        enginePending: outcome.enginePending.length,
        requiresScope: outcome.requiresScope.length,
        blocked: outcome.blocked.length,
      };
      setRuleSaveSummary(summary);

      if (active > 0) {
        setHasConfirmedRules(true);
        toast.success(`${active} active requirements ready for IFC validation`, {
          description: `${summary.enginePending} engine pending · ${summary.requiresScope} require scope · ${summary.blocked} blocked`,
          duration: 8000,
        });
        requestAnimationFrame(() => {
          setStep(2);
        });
      } else {
        toast.warning('No active requirements are ready for IFC validation', {
          description: `${summary.enginePending} engine pending · ${summary.requiresScope} require scope · ${summary.blocked} blocked`,
          duration: 12000,
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setConfirming(false);
    }
  };

  // ── Step 2: Run IFC validation ──
  const [namingMismatch, setNamingMismatch] = useState(null); // {errors, expected}
  // Survives past the initial already_analyzed / naming-mismatch response so
  // "Run anyway" / "Validate anyway" can reuse the SAME shared upload
  // instead of re-chunking a >20MB IFC over the wire again. Reset whenever
  // a different IFC file is selected (see the IFC MiniDropZone onDrop/onRemove).
  const chunkedUploadIdRef = useRef(null);
  const uploadFileChunked = async (file, fileType, uploadId) => {
    return uploadFileInChunks({
      file,
      fileType,
      uploadId,
      projectId,
      apiUrl: API_URL,
      onProgress: ({ percent, status }) => {
        setUploadStep(status === 'finalizing'
          ? 'Finalizing IFC...'
          : `Uploading ${fileType.toUpperCase()} (${percent}% server-confirmed)...`);
      },
    });
  };

  const runAudit = async ({ force = false, confirmMismatch = false } = {}) => {
    if (ifcFiles.length === 0) { setError('Upload an IFC file'); return; }
    setUploading(true); setError('');
    setDuplicateAuditInfo(null);
    setNamingMismatch(null);
    try {
      const ifc = ifcFiles[0].file;
      let audit_id;
      if (ifc.size > 20 * 1024 * 1024) {
        const uid = chunkedUploadIdRef.current || Math.random().toString(36).substring(2, 10);
        chunkedUploadIdRef.current = uid;
        await uploadFileChunked(ifc, 'ifc', uid);
        for (const f of omFiles) await uploadFileChunked(f.file, 'om', uid);
        setUploadStep('Starting audit...');
        const form = new FormData();
        form.append('upload_id', uid);
        form.append('project_id', projectId);
        form.append('validation_profile', JSON.stringify(validationProfile));
        if (force) form.append('force', 'true');
        if (confirmMismatch) form.append('confirm_mismatch', 'true');
        const resp = await apiFetch(`${API_URL}/api/audits/start-from-chunks`, { method: 'POST', body: form });
        const text = await resp.text();
        try {
          const data = JSON.parse(text);
          if (resp.status === 409 && data.mismatch) {
            setNamingMismatch({
              errors: data.errors || [],
              expected: data.project_code_expected,
              source: 'chunked',
            });
            return;
          }
          if (!resp.ok) throw new Error(data.detail || `Server error (${resp.status})`);
          // Duplicate detection: same IFC hash + same validation profile
          // already ran successfully. Don't auto-redirect — let the user
          // pick "View existing" or "Run anyway" via the dialog.
          if (data.already_analyzed) {
            setDuplicateAuditInfo({
              audit_id: data.audit_id,
              ifc_file_name: data.ifc_file_name,
              created_at: data.created_at,
              total_elements: data.total_elements,
              validated_elements: data.validated_elements,
              prior_profile: data.validation_profile || {},
              source: 'chunked',
            });
            return;
          }
          audit_id = data.audit_id;
        } catch (parseErr) {
          if (!resp.ok) throw new Error(`Server error (${resp.status})`);
          throw parseErr;
        }
      } else {
        setUploadStep('Uploading...');
        const formData = new FormData();
        formData.append('ifc_file', ifc);
        omFiles.forEach(f => formData.append('om_files', f.file));
        formData.append('project_id', projectId);
        formData.append('validation_profile', JSON.stringify(validationProfile));
        if (force) formData.append('force', 'true');
        if (confirmMismatch) formData.append('confirm_mismatch', 'true');
        const resp = await apiFetch(`${API_URL}/api/audits/upload`, { method: 'POST', body: formData });
        const text = await resp.text();
        try {
          const data = JSON.parse(text);
          if (resp.status === 409 && data.mismatch) {
            setNamingMismatch({
              errors: data.errors || [],
              expected: data.project_code_expected,
              upload_ref: data.upload_ref,
              source: 'single',
            });
            return;
          }
          if (!resp.ok) throw new Error(data.detail || `Upload failed (${resp.status})`);
          if (data.already_analyzed) {
            setDuplicateAuditInfo({
              audit_id: data.audit_id,
              ifc_file_name: data.ifc_file_name,
              created_at: data.created_at,
              total_elements: data.total_elements,
              validated_elements: data.validated_elements,
              prior_profile: data.validation_profile || {},
              upload_ref: data.upload_ref,
              source: 'single',
            });
            return;
          }
          audit_id = data.audit_id;
        } catch (parseErr) {
          if (!resp.ok) throw new Error(`Upload failed (${resp.status})`);
          throw parseErr;
        }
      }
      // Hand off to the dedicated AHA Quality Gate progress page
      // (circular gauge + ETA) — same UX as the New IFC Validation
      // flow. The progress page owns its polling and auto-navigates
      // to /results when the audit reaches a terminal state. This
      // replaces the old inline 10-minute polling loop, whose brittle
      // error handling could show a failure on a transient network
      // blip while the backend kept running.
      fetchPreviousIfcUploads();
      navigate(`/audits/${audit_id}/progress?project=${encodeURIComponent(projectId)}`);
    } catch (err) { setError(err.message); }
    finally { setUploading(false); setUploadStep(''); }
  };

  // Lean retry — reuses the IFC already saved by a prior /upload call that
  // returned already_analyzed / a naming-mismatch 409, instead of re-posting
  // the full IFC body via runAudit(). Falls back to runAudit() on a 410
  // (upload_ref stale/swept) so the retry never silently does nothing.
  const rerunFromUploadRef = async (uploadRef, { force = false, confirmMismatch = false } = {}) => {
    setUploading(true); setError('');
    setDuplicateAuditInfo(null);
    setNamingMismatch(null);
    try {
      setUploadStep('Starting audit...');
      const formData = new FormData();
      formData.append('project_id', projectId);
      formData.append('validation_profile', JSON.stringify(validationProfile));
      formData.append('upload_ref', uploadRef);
      if (force) formData.append('force', 'true');
      if (confirmMismatch) formData.append('confirm_mismatch', 'true');
      const resp = await apiFetch(`${API_URL}/api/audits/upload`, { method: 'POST', body: formData });
      if (resp.status === 410) {
        await runAudit({ force, confirmMismatch });
        return;
      }
      const text = await resp.text();
      const data = JSON.parse(text);
      if (resp.status === 409 && data.mismatch) {
        setNamingMismatch({
          errors: data.errors || [],
          expected: data.project_code_expected,
          upload_ref: data.upload_ref,
          source: 'single',
        });
        return;
      }
      if (!resp.ok) throw new Error(data.detail || `Upload failed (${resp.status})`);
      if (data.already_analyzed) {
        setDuplicateAuditInfo({
          audit_id: data.audit_id,
          ifc_file_name: data.ifc_file_name,
          created_at: data.created_at,
          total_elements: data.total_elements,
          validated_elements: data.validated_elements,
          prior_profile: data.validation_profile || {},
          upload_ref: data.upload_ref,
          source: 'single',
        });
        return;
      }
      fetchPreviousIfcUploads();
      navigate(`/audits/${data.audit_id}/progress?project=${encodeURIComponent(projectId)}`);
    } catch (err) { setError(err.message); }
    finally { setUploading(false); setUploadStep(''); }
  };

  const canUseLeanRerun = (ref, source) => !!ref && source === 'single' && omFiles.length === 0;

  // Lean chunked retry — reuses the SAME upload_id from the original
  // >20MB chunked attempt, so start-from-chunks re-reads the already
  // fully-reassembled ifc/om files straight from chunk_dir instead of
  // uploadFileChunked() re-chunking the whole file over the wire again.
  // Falls back to runAudit() (full re-chunk) on 404 (chunk_dir swept/missing)
  // so the retry never silently does nothing.
  const rerunFromChunkedUploadId = async (uploadId, { force = false, confirmMismatch = false } = {}) => {
    setUploading(true); setError('');
    setDuplicateAuditInfo(null);
    setNamingMismatch(null);
    try {
      setUploadStep('Starting audit...');
      const form = new FormData();
      form.append('upload_id', uploadId);
      form.append('project_id', projectId);
      form.append('validation_profile', JSON.stringify(validationProfile));
      if (force) form.append('force', 'true');
      if (confirmMismatch) form.append('confirm_mismatch', 'true');
      const resp = await apiFetch(`${API_URL}/api/audits/start-from-chunks`, { method: 'POST', body: form });
      if (resp.status === 404) {
        await runAudit({ force, confirmMismatch });
        return;
      }
      const text = await resp.text();
      const data = JSON.parse(text);
      if (resp.status === 409 && data.mismatch) {
        setNamingMismatch({
          errors: data.errors || [],
          expected: data.project_code_expected,
          source: 'chunked',
        });
        return;
      }
      if (!resp.ok) throw new Error(data.detail || `Server error (${resp.status})`);
      if (data.already_analyzed) {
        setDuplicateAuditInfo({
          audit_id: data.audit_id,
          ifc_file_name: data.ifc_file_name,
          created_at: data.created_at,
          total_elements: data.total_elements,
          validated_elements: data.validated_elements,
          prior_profile: data.validation_profile || {},
          source: 'chunked',
        });
        return;
      }
      fetchPreviousIfcUploads();
      navigate(`/audits/${data.audit_id}/progress?project=${encodeURIComponent(projectId)}`);
    } catch (err) { setError(err.message); }
    finally { setUploading(false); setUploadStep(''); }
  };

  const canUseLeanChunkedRerun = (source) => source === 'chunked' && !!chunkedUploadIdRef.current;

  // ── Previously-uploaded IFC picker (Step 2) ──
  const [previousIfcUploads, setPreviousIfcUploads] = useState([]);
  const [selectedPreviousUpload, setSelectedPreviousUpload] = useState(null);

  const fetchPreviousIfcUploads = useCallback(async () => {
    try {
      const resp = await apiFetch(`${API_URL}/api/audits/ifc-uploads?project_id=${projectId}`);
      if (resp.ok) {
        const data = await resp.json();
        setPreviousIfcUploads(data.uploads || []);
      }
    } catch (err) { console.warn('fetchPreviousIfcUploads failed:', err); }
  }, [projectId]);

  useEffect(() => { fetchPreviousIfcUploads(); }, [fetchPreviousIfcUploads]);

  // Mutually exclusive with dropping/browsing a new IFC file (see the
  // MiniDropZone onDrop below, which clears this the other way).
  const handleSelectPreviousUpload = (upload) => {
    if (upload) {
      chunkedUploadIdRef.current = null;
      setIfcFiles([]);
    }
    setSelectedPreviousUpload(upload);
  };

  // Runs (or retries) an audit against a DIFFERENT past audit's IFC via
  // source_audit_id — no file body, no re-upload. On 410 (file swept /
  // hash changed) there is no in-browser File to fall back to, since the
  // whole point of the picker is to avoid ever holding those bytes again,
  // so we just surface the error and ask the user to re-upload.
  const runFromPreviousUpload = async (sourceAuditId, { force = false, confirmMismatch = false } = {}) => {
    setUploading(true); setError('');
    setDuplicateAuditInfo(null);
    setNamingMismatch(null);
    try {
      setUploadStep('Starting audit...');
      const form = new FormData();
      form.append('project_id', projectId);
      form.append('validation_profile', JSON.stringify(validationProfile));
      form.append('source_audit_id', sourceAuditId);
      if (force) form.append('force', 'true');
      if (confirmMismatch) form.append('confirm_mismatch', 'true');
      const resp = await apiFetch(`${API_URL}/api/audits/upload`, { method: 'POST', body: form });
      if (resp.status === 410) {
        setSelectedPreviousUpload(null);
        fetchPreviousIfcUploads();
        setError('This previously uploaded IFC is no longer available on the server. Please upload the file again.');
        return;
      }
      const text = await resp.text();
      const data = JSON.parse(text);
      if (resp.status === 409 && data.mismatch) {
        setNamingMismatch({
          errors: data.errors || [],
          expected: data.project_code_expected,
          source_audit_id: data.source_audit_id,
        });
        return;
      }
      if (!resp.ok) throw new Error(data.detail || `Upload failed (${resp.status})`);
      if (data.already_analyzed) {
        setDuplicateAuditInfo({
          audit_id: data.audit_id,
          ifc_file_name: data.ifc_file_name,
          created_at: data.created_at,
          total_elements: data.total_elements,
          validated_elements: data.validated_elements,
          prior_profile: data.validation_profile || {},
          source_audit_id: data.source_audit_id,
        });
        return;
      }
      navigate(`/audits/${data.audit_id}/progress?project=${encodeURIComponent(projectId)}`);
    } catch (err) { setError(err.message); }
    finally { setUploading(false); setUploadStep(''); }
  };


  if (loading && !project) {
    return (<DashboardLayout><div className="flex items-center justify-center h-64" data-testid="project-detail-loading">
      <Loader2 className="h-8 w-8 animate-spin text-accent" />
    </div></DashboardLayout>);
  }

  if (!project) {
    return (<DashboardLayout><div className="text-center py-16">
      <h2 className="text-xl font-semibold mb-4">Project not found</h2>
      <Button onClick={() => navigate('/projects')}>Back to Projects</Button>
    </div></DashboardLayout>);
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        {/* BEP Extracted Rules — read-only viewer modal */}
        {showBepRules && realBep && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowBepRules(false)}
            data-testid="bep-rules-modal">
            <Card className="max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <CardHeader className="pb-3 border-b border-border">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <ListChecks className="h-4 w-4 text-accent" />
                      Stored extraction snapshot
                    </CardTitle>
                    <CardDescription className="text-xs mt-1">
                      From <strong>{realBep.file_name}</strong>
                      {realBep.document_version && <> • Version {realBep.document_version}</>}
                      {realBep.analyzed_at && <> • Analyzed {new Date(realBep.analyzed_at).toLocaleDateString()}</>}
                    </CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setShowBepRules(false)}
                    data-testid="close-bep-rules-modal">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="overflow-y-auto flex-1 pt-3">
                <input
                  type="text"
                  placeholder="Search rules..."
                  value={bepRulesFilter}
                  onChange={(e) => setBepRulesFilter(e.target.value)}
                  className="w-full h-9 px-3 mb-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  data-testid="bep-rules-search"
                />
                <div className="space-y-1.5">
                  {(realBep.candidate_rules || [])
                    .filter(r => {
                      if (!bepRulesFilter.trim()) return true;
                      const q = bepRulesFilter.toLowerCase();
                      return JSON.stringify(r).toLowerCase().includes(q);
                    })
                    .map((rule, idx) => (
                      <div key={rule.rule_id || `bep-rule-${idx}`}
                        className="flex items-start gap-3 p-2.5 rounded-lg border border-border bg-card"
                        data-testid={`bep-rule-${idx}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <Badge variant="outline" className="text-[10px] px-1.5">{rule.category || 'GENERAL'}</Badge>
                            <Badge className={`text-[10px] px-1.5 border ${SEVERITY_COLORS[rule.severity] || SEVERITY_COLORS.major}`}>
                              {rule.severity || 'major'}
                            </Badge>
                            {rule.check_type && (
                              <Badge variant="outline" className="text-[10px] px-1.5">{rule.check_type}</Badge>
                            )}
                            {rule.selectable === false && rule.gate_status && (
                              <Badge className="text-[10px] px-1.5 bg-amber-500/10 text-amber-700 border border-amber-500/30"
                                     data-testid={`bep-rule-gate-${idx}`}>
                                {rule.gate_status}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-foreground">{rule.description || '(no description)'}</p>
                          {(rule.field_to_check || rule.expected_pattern) && (
                            <div className="flex flex-wrap gap-x-4 mt-1">
                              {rule.field_to_check && (
                                <p className="text-[11px] text-muted-foreground">Field: <span className="font-mono text-foreground">{rule.field_to_check}</span></p>
                              )}
                              {rule.expected_pattern && (
                                <p className="text-[11px] text-muted-foreground">Expected: <span className="text-foreground break-all">{rule.expected_pattern}</span></p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
                {(!realBep.candidate_rules || realBep.candidate_rules.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No stored rule details. Re-extract this BEP to capture full rule text.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/projects')} className="mb-3" data-testid="back-to-projects">
            <ArrowLeft className="h-4 w-4 mr-1" /> Projects
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-accent/10"><Building2 className="h-7 w-7 text-accent" /></div>
            <div>
              <h1 className="text-2xl font-bold text-foreground" data-testid="project-name">{project.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline">{project.code}-{project.originator}</Badge>
                {dbProject?.is_shared_demo ? (
                  <Badge
                    variant="outline"
                    className="gap-1 border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                    data-testid="project-demo-access-badge"
                  >
                    <LockKeyhole className="h-3 w-3" />
                    {isReadOnly ? 'Shared demo · Read-only' : 'Shared demo · Owner'}
                  </Badge>
                ) : (
                  <Badge className="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border border-[hsl(var(--success))]/30 text-xs">Active</Badge>
                )}
                {lastReviewDate && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" /> Last review: {lastReviewDate.toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {isReadOnly && (
          <Alert className="mb-6 border-amber-500/40 bg-amber-500/5" data-testid="project-read-only-banner">
            <LockKeyhole className="h-4 w-4 text-amber-700 dark:text-amber-300" />
            <AlertDescription>
              <strong>Read-only demo.</strong> You can inspect this project's BEP, rules, audits, AIM records, results, and reports. Only the project owner can change data or start new processing.
            </AlertDescription>
          </Alert>
        )}

        {/* Step indicator — both clickable */}
        <div className="flex items-center gap-3 mb-6">
          <div
            onClick={() => setStep(1)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer
              ${step === 1 ? 'bg-accent text-accent-foreground' : hasConfirmedRules ? 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
          >
            {hasConfirmedRules ? <CheckCircle2 className="h-4 w-4" /> : <span className="h-5 w-5 rounded-full border-2 flex items-center justify-center text-xs">1</span>}
            BEP Rules Review
            <ModelBadge />
            {hitlPending > 0 && (
              <span
                className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border border-[hsl(var(--warning))]/40"
                data-testid="hitl-pending-indicator"
                title={`${hitlPending} rule(s) awaiting HITL applicability review`}
              >
                {hitlPending} HITL
              </span>
            )}
          </div>
          <div className="h-px w-6 bg-border" />
          <div
            onClick={() => setStep(2)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer
              ${step === 2 ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
            data-testid="step-2-tab"
          >
            <span className="h-5 w-5 rounded-full border-2 flex items-center justify-center text-xs">2</span>
            IFC Validation
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4" data-testid="project-error">
            <AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Sprint 4 — Project Dashboard (bar / donut / sparkline).
            Renders an empty-state card when the project has no completed
            audits yet, so we don't conditionally render and avoid layout shift. */}
        <div className="mb-6" data-testid="project-dashboard-section">
          <ProjectDashboardCharts projectId={projectId} />
        </div>

        {/* BEP Document Info — reads real data from bep_registry only.
            Hidden completely when no BEP has been uploaded for this project.
            Shows loading skeleton during fetch and error alert on fetch failure. */}
        {(() => {
          // Error state — short red alert (still visible so user knows there's a problem)
          if (bepError) {
            return (
              <Alert variant="destructive" className="mb-6" data-testid="bep-details-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {bepError}. <button onClick={() => window.location.reload()} className="underline">Retry</button>
                </AlertDescription>
              </Alert>
            );
          }
          // Loading skeleton
          if (bepLoading) {
            return (
              <Card className="card-elevated mb-6" data-testid="bep-details-loading">
                <CardContent className="py-4 flex items-center gap-3">
                  <Loader2 className="h-4 w-4 animate-spin text-accent" />
                  <p className="text-sm text-muted-foreground">Loading BEP details...</p>
                </CardContent>
              </Card>
            );
          }
          // No BEP uploaded → hide the panel entirely (Req 5).
          // The BEP upload section in Step 1 remains visible regardless.
          if (!realBep) return null;
          
          // Real BEP from bep_registry
          return (
            <Card className="card-elevated mb-6" data-testid="bep-details-real">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                      <FileText className="h-4 w-4 text-accent shrink-0" /> BEP Document Details
                      <button
                        className="text-xs text-accent hover:underline font-normal"
                        onClick={() => navigate(`/projects/${projectId}/bep-readiness`)}
                        data-testid="view-readiness-dashboard-link">
                        View Readiness Dashboard →
                      </button>
                      <Badge className={`text-[10px] border ${
                        realBep.has_stored_rules
                          ? 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30'
                          : 'bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30'
                      }`} data-testid="bep-status-badge">
                        {realBep.has_stored_rules ? 'Analyzed' : 'Legacy — rules not stored'}
                      </Badge>
                      <ModelBadge />
                    </CardTitle>
                    <CardDescription className="text-xs mt-1">
                      {realBep.file_name}
                      {realBep.document_version && (
                        <span className="ml-2 text-foreground">• Version: <strong>{realBep.document_version}</strong></span>
                      )}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Document ID</p>
                    <p className="font-mono text-xs truncate" title={realBep.file_hash}>{(realBep.file_hash || '').slice(0, 16)}...</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Analyzed</p>
                    <p className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {realBep.analyzed_at ? new Date(realBep.analyzed_at).toLocaleString() : '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Originator</p>
                    <p className="flex items-center gap-1 truncate" title={realBep.extracted_by || realBep.project_originator}>
                      <Shield className="h-3 w-3" />
                      {realBep.extracted_by ? maskEmail(realBep.extracted_by) : (realBep.project_originator || '—')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Last Review</p>
                    <p className="flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {lastReviewDate ? lastReviewDate.toLocaleDateString() : 'No audits yet'}
                    </p>
                  </div>
                </div>
                <div className="border-t border-border mt-3 pt-3 flex items-center gap-6 text-xs text-muted-foreground flex-wrap">
                  <span className="flex items-center gap-1">
                    <Layers className="h-3 w-3" /> Stored extraction snapshot
                  </span>
                  {realBep.processing_time_seconds > 0 && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Processing: <strong className="text-foreground">{realBep.processing_time_seconds}s</strong>
                    </span>
                  )}
                  {realBep.manifest && realBep.manifest.project_code && (
                    <span>Manifest project_code: <strong className="text-foreground">{realBep.manifest.project_code}</strong></span>
                  )}
                  {realBep.manifest && realBep.manifest.originator && (
                    <span>Manifest originator: <strong className="text-foreground">{realBep.manifest.originator}</strong></span>
                  )}
                  {realBep.has_stored_rules && (
                    <>
                      <Button size="sm" variant="outline" className="ml-auto h-7"
                        onClick={() => setShowBepRules(true)}
                        data-testid="view-extracted-rules-btn">
                        <ListChecks className="h-3.5 w-3.5 mr-1" /> View stored extraction snapshot
                      </Button>
                      <Button size="sm" variant="outline" className="h-7"
                        onClick={() => exportRules('xlsx')}
                        data-testid="export-rules-xlsx-btn"
                        title="Download rules as an Excel workbook">
                        <Download className="h-3.5 w-3.5 mr-1" /> Excel
                      </Button>
                      <Button size="sm" variant="outline" className="h-7"
                        onClick={() => exportRules('csv')}
                        data-testid="export-rules-csv-btn"
                        title="Download rules as CSV">
                        <Download className="h-3.5 w-3.5 mr-1" /> CSV
                      </Button>
                      <Button size="sm" variant="outline" className="h-7"
                        onClick={() => exportRules('pdf')}
                        data-testid="export-rules-pdf-btn"
                        title="Download a printable PDF report of the active rules only (description, field, category, severity — grouped by category).">
                        <Download className="h-3.5 w-3.5 mr-1" /> Active Rules (PDF)
                      </Button>
                      {!isReadOnly && <Button size="sm" className="h-7 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white"
                        onClick={downloadAiRulesReport}
                        disabled={aiReportLoading}
                        data-testid="ai-rules-report-btn"
                        title="GPT-4o writes a strategic analysis PDF of the active BEP rules: executive summary, per-category coverage, gaps, recommended additions, risk highlights. Advisory only.">
                        {aiReportLoading ? (
                          <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        ) : (
                          <Sparkles className="h-3.5 w-3.5 mr-1" />
                        )}
                        {aiReportLoading ? 'Generating…' : 'AI Rules Analysis (PDF)'}
                      </Button>}
                      <IdsExportImportButtons
                        projectId={projectId}
                        projectName={project?.name}
                        projectCode={project?.code}
                        readOnly={isReadOnly}
                        onImported={(count) => {
                          // After a successful IDS import, refresh BEP details
                          // so the new rule count is reflected in the strip.
                          setError('');
                          fetchData();
                        }}
                      />
                    </>
                  )}
                  {!realBep.has_stored_rules && realBep.rules_count > 0 && (
                    <span className="ml-auto text-[hsl(var(--warning))] flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> Rule details not stored — re-extract to capture
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Readiness audit (advisory) — visible on both steps, below BEP details. */}
        <div className="mb-6">
          <BEPReadinessPanel projectId={projectId} hasBep={!!realBep} readOnly={isReadOnly}
            localBepFile={bepFiles[0]?.file || null} />
        </div>

        {/* ── Extraction-status banner (Feb 2026) ──
            Silent failures — a registry stuck in ``failed`` /
            ``failed_activation`` OR the "no eligible current registry"
            case — used to be visible only in server logs. Surface both
            here (above the step tabs) so the user knows why Step 1
            shows nothing / the wrong thing regardless of which step
            the page opened on. */}
        {(() => {
          const status = realBep?.extraction_status;
          const jobErr = realBep?.latest_job?.error_summary;
          const hardFail =
            status === 'failed' || status === 'failed_activation';
          const noEligible = realBep && !hasEligibleCurrent;
          if (!hardFail && !noEligible) return null;
          const headline = hardFail
            ? `BEP extraction ${status.replace('_', ' ')}`
            : 'No eligible current BEP registry';
          return (
            <div
              className="mb-6 rounded-md border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/5 p-4"
              data-testid="bep-extraction-failure-banner"
            >
              <div className="flex items-start gap-3">
                <AlertCircle
                  className="h-5 w-5 text-[hsl(var(--destructive))] mt-0.5 flex-shrink-0"
                  aria-hidden="true"
                />
                <div className="flex-1 min-w-0 space-y-1">
                  <p
                    className="text-sm font-semibold text-[hsl(var(--destructive))]"
                    data-testid="bep-extraction-banner-headline"
                  >
                    {headline}
                  </p>
                  {realBep?.file_name && (
                    <p className="text-xs text-muted-foreground">
                      Registry: <span className="font-mono">{realBep.file_name}</span>
                      {realBep.document_version && (
                        <> · v{realBep.document_version}</>
                      )}
                      {status && (
                        <> · status: <span className="font-mono">{status}</span></>
                      )}
                    </p>
                  )}
                  {jobErr && (
                    <p
                      className="text-xs text-muted-foreground whitespace-pre-wrap break-words"
                      data-testid="bep-extraction-banner-error"
                    >
                      {jobErr}
                    </p>
                  )}
                  {!jobErr && noEligible && (
                    <p className="text-xs text-muted-foreground">
                      No BEP registry passes the activation guard for this
                      project — the extraction failed before rules could be
                      promoted. Re-upload the BEP below to run a fresh
                      extraction.
                    </p>
                  )}
                  {!isReadOnly && <div className="pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setStep(1);
                        setCandidateRules([]);
                        setBepFiles([]);
                        setBepDuplicate(null);
                        setTimeout(
                          () =>
                            document
                              .querySelector('[data-testid="step1-bep-drop"]')
                              ?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start',
                              }),
                          150,
                        );
                      }}
                      data-testid="bep-extraction-rerun-btn"
                    >
                      Re-run extraction
                    </Button>
                  </div>}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── STEP 1: BEP Rules Review ── */}
        {step === 1 && (
          isReadOnly ? (
            <Alert className="border-amber-500/30 bg-amber-500/5" data-testid="step-one-read-only-notice">
              <LockKeyhole className="h-4 w-4 text-amber-700 dark:text-amber-300" />
              <AlertDescription>
                BEP extraction and rule review controls are owner-only. Use the stored extraction snapshot and exports above to inspect this demo.
              </AlertDescription>
            </Alert>
          ) : (
          <div className="space-y-4">
            <RuleSaveConfirmation summary={ruleSaveSummary} />
            {/* HITL applicability queue — surfaces rules the engine couldn't pin. */}
            <HitlReviewQueue
              projectId={projectId}
              onQueueChange={setHitlPending}
              onPromoted={() => {
                // A promoted rule joins confirmed_rules — refresh the
                // confirmed list + the page's project totals so the
                // KPI badge updates immediately.
                fetchConfirmedRules?.();
                fetchData?.();
              }}
            />

            {/* Live hologram — covers BOTH fresh extraction and force
                re-extract, since both set the same `extracting`/`extractStep`
                state via extractRules(). */}
            {extracting && <BepExtractionHologram step={extractStep} />}

            {/* Duplicate BEP Alert */}
            {bepDuplicate && (
              <Alert className="border-[hsl(var(--warning))]" data-testid="bep-duplicate-alert">
                <AlertCircle className="h-4 w-4 text-[hsl(var(--warning))]" />
                <AlertDescription>
                  <p className="font-semibold mb-1">This BEP has already been analyzed</p>
                  <p className="text-sm text-muted-foreground mb-1">
                    <strong>{bepDuplicate.bep_file_name}</strong>
                    {bepDuplicate.document_version && <> (version <strong>{bepDuplicate.document_version}</strong>)</>}
                    {' '}was processed on{' '}
                    {new Date(bepDuplicate.analyzed_at).toLocaleDateString()} — {bepDuplicate.rules_count} rules were extracted.
                  </p>
                  {bepDuplicate.has_stored_rules === false && (
                    <p className="text-xs text-[hsl(var(--warning))] mb-3 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> Rule details are not stored for this run. Click <strong>Re-extract Anyway</strong> to capture them.
                    </p>
                  )}
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => { setBepDuplicate(null); setBepFiles([]); }}
                      data-testid="upload-new-version-btn">
                      Upload New Version
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setShowReExtract(true)}
                      disabled={extracting}
                      data-testid="force-reextract-btn">
                      {extracting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileText className="h-3.5 w-3.5 mr-1" />}
                      Re-extract Anyway
                    </Button>
                    <Button size="sm" onClick={() => { setBepDuplicate(null); setStep(2); }}
                      data-testid="continue-to-ifc-btn">
                      Continue to IFC Validation
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Typed-confirmation gate for the destructive force re-extract. */}
            <ReExtractWarningDialog
              open={showReExtract}
              onOpenChange={setShowReExtract}
              projectId={projectId}
              registryId={bepDuplicate?.registry_id}
              fileName={bepDuplicate?.bep_file_name}
              onConfirm={() => extractRules({ force: true })}
            />

            <div id="bep-step1-anchor" />
            {!bepDuplicate && candidateRules.length === 0 ? (
              /* Upload BEP to extract rules */
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="text-base">Step 1: Extract Rules from BEP</CardTitle>
                  <CardDescription>Upload the BEP document. AI will extract validation rules for your review.</CardDescription>
                </CardHeader>
                <CardContent>
                  <MiniDropZone
                    label="BEP Document (.pdf)" icon={FileText}
                    accept={{ 'application/pdf': ['.pdf'] }}
                    files={bepFiles}
                    onDrop={(accepted) => setBepFiles(accepted.map(makeFileObj))}
                    onRemove={() => setBepFiles([])}
                    testId="step1-bep-drop"
                  />
                  {extracting && (
                    <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>See live progress above.</span>
                    </div>
                  )}
                  <Button onClick={extractRules} disabled={extracting || bepFiles.length === 0}
                    className="mt-4 w-full" data-testid="extract-rules-btn">
                    {extracting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Extracting...</> :
                      <><FileText className="mr-2 h-4 w-4" /> Extract Rules from BEP</>}
                  </Button>
                  {hasConfirmedRules && (
                    <Button variant="outline" onClick={() => setStep(2)} className="mt-2 w-full">
                      Skip — Use Previously Confirmed Rules
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              /* Review extracted rules */
              <Card className="card-elevated">
                <CardHeader>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <CardTitle className="text-base">Review Extracted Rules</CardTitle>
                      <CardDescription>
                        {selectableRules.length} AI/gate eligible · {humanRules.length} available for BIM review · {candidateRules.length} total
                      </CardDescription>
                    </div>
                    <Button size="sm" variant="outline"
                      onClick={() => { setCandidateRules([]); setBepFiles([]); setBepDuplicate(null); }}
                      data-testid="upload-different-bep-btn"
                      title="Go back to the BEP upload zone — re-uploading the same document offers a guarded re-extract.">
                      <FileText className="h-3.5 w-3.5 mr-1" /> Upload / Re-extract BEP
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Manifest summary */}
                  {manifest && (
                    <div className="mb-4 p-3 bg-muted/50 rounded-lg text-xs space-y-1">
                      <p className="font-semibold text-foreground mb-1">Detected Project Settings:</p>
                      {manifest.project_code && <p>Project Code: <strong>{manifest.project_code}</strong></p>}
                      {manifest.originator && <p>Originator: <strong>{manifest.originator}</strong></p>}
                      {manifest.valid_types && <p>Valid Types: <strong>{manifest.valid_types.join(', ')}</strong></p>}
                      {manifest.mandatory_fm_params && <p>FM Params: <strong>{manifest.mandatory_fm_params.join(', ')}</strong></p>}
                    </div>
                  )}

                  {/* Select all / deselect all */}
                  <div className="flex items-center gap-3 mb-3">
                    <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-rules">
                      <SquareCheck className="h-3.5 w-3.5 mr-1" /> Select All
                    </Button>
                    <Button variant="outline" size="sm" onClick={deselectAll} data-testid="deselect-all-rules">
                      <Square className="h-3.5 w-3.5 mr-1" /> Deselect All
                    </Button>
                    <span className="text-xs text-muted-foreground ml-auto" data-testid="review-selection-count">
                      {humanRules.filter(r => r.selected).length} of {humanRules.length} selected
                    </span>
                  </div>

                  {/* Rules checklist */}
                  <div className="space-y-1.5 max-h-[500px] overflow-y-auto pr-1">
                    {selectableRules.map((rule) => (
                      <div
                        key={reviewIdentity(rule)}
                        onClick={() => toggleRule(reviewIdentity(rule))}
                        className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors border
                          ${rule.selected ? 'bg-accent/5 border-accent/30' : 'bg-muted/20 border-transparent opacity-60'}`}
                        data-testid={`rule-${reviewIdentity(rule)}`}
                      >
                        <div className="mt-0.5">
                          {rule.selected ?
                            <SquareCheck className="h-4 w-4 text-accent" /> :
                            <Square className="h-4 w-4 text-muted-foreground" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            {/* 1-based numeric index for quick reference in review */}
                            <span
                              className="inline-flex items-center justify-center min-w-[1.75rem] h-5 px-1.5 rounded-md text-[10px] font-semibold tabular-nums bg-muted text-muted-foreground"
                              data-testid={`rule-index-${reviewIdentity(rule)}`}
                              title={rule.candidate_rule_id || rule.id}
                            >
                              {rule.review_ref || rule.id}
                            </span>
                            <Badge variant="outline" className="text-[10px] px-1.5">{rule.category}</Badge>
                            <Badge className={`text-[10px] px-1.5 border ${SEVERITY_COLORS[rule.severity] || SEVERITY_COLORS.major}`}>
                              {rule.severity}
                            </Badge>
                            {rule.type === 'manifest' && <Badge className="text-[10px] px-1.5 bg-accent/10 text-accent border border-accent/30">manifest</Badge>}
                            {rule.iso_reference && <Badge variant="outline" className="text-[10px] px-1.5">{rule.iso_reference}</Badge>}
                            <ApplicabilityChip applicability={rule._applicability} />
                          </div>
                          <p className="text-sm text-foreground">{rule.description}</p>
                          <ReviewRuntimeStatus rule={rule} onScopeChange={setReviewScope} />
                          <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
                            {rule.field_to_check && (
                              <p className="text-[11px] text-muted-foreground">Field: <span className="font-mono text-foreground">{rule.field_to_check}</span></p>
                            )}
                            {rule.applies_to && rule.applies_to !== 'all' && (
                              <p className="text-[11px] text-muted-foreground">Applies to: <span className="text-foreground">{typeof rule.applies_to === 'string' ? rule.applies_to : JSON.stringify(rule.applies_to)}</span></p>
                            )}
                            {rule.expected_value && (
                              <p className="text-[11px] text-muted-foreground">Expected: <span className="text-foreground">{typeof rule.expected_value === 'string' ? rule.expected_value : JSON.stringify(rule.expected_value)}</span></p>
                            )}
                          </div>
                          {rule.source_text && (
                            <p className="text-[11px] text-muted-foreground mt-0.5 truncate italic">&quot;{rule.source_text}&quot;</p>
                          )}
                          {/* Terra/Sol provenance (Aug 2026, Item 2).
                              When the rule carries a Sol verdict, show
                              the verdict badge, review reason, and the
                              exact substring Sol quoted from the source
                              block so the reviewer can verify without
                              opening the PDF. */}
                          {rule.verifier_verdict && (
                            <div className="mt-1.5 space-y-1 border-l-2 border-accent/30 pl-2">
                              <div className="flex items-center gap-1.5">
                                <Badge
                                  variant={rule.verifier_verdict === 'accepted' ? 'default' : 'outline'}
                                  className="text-[10px] px-1.5"
                                   data-testid={`verifier-verdict-${reviewIdentity(rule)}`}
                                >
                                  Sol: {rule.verifier_verdict}
                                </Badge>
                              </div>
                              {rule.verifier_reason && (
                                <p className="text-[11px] text-muted-foreground"
                                   data-testid={`verifier-reason-${reviewIdentity(rule)}`}>
                                  <span className="font-medium">Reason:</span> {rule.verifier_reason}
                                </p>
                              )}
                              {rule.exact_evidence && (
                                <p className="text-[11px] text-muted-foreground italic"
                                   data-testid={`verifier-evidence-${reviewIdentity(rule)}`}>
                                  <span className="font-medium not-italic">Evidence:</span> &ldquo;{rule.exact_evidence}&rdquo;
                                </p>
                              )}
                            </div>
                          )}
                          {!rule.selected && (
                            <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>
                              <select
                                value={rule.review_status || 'not_selected'}
                                onChange={(e) => setCandidateDecision(reviewIdentity(rule), e.target.value)}
                                className="text-[11px] h-6 px-1 rounded border border-input bg-background text-muted-foreground cursor-pointer"
                                data-testid={`review-decision-${reviewIdentity(rule)}`}
                              >
                                <option value="not_selected">Not selected</option>
                                <option value="deferred">Deferred</option>
                                <option value="needs_clarification">Needs clarification</option>
                              </select>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* P02 Batch 2 — gated (non-selectable) candidates,
                        collapsed by gate status. Expanding shows each
                        entry with its status chip and verbatim reason. */}
                    {GATE_GROUPS.map(([status, label]) => {
                      const group = gatedRules.filter(r => (r.display_gate_status || r.gate_status) === status);
                      if (group.length === 0) return null;
                      const open = !!expandedGateGroups[status];
                      return (
                        <div key={status} className="mt-2">
                          <button
                            type="button"
                            onClick={() => setExpandedGateGroups(p => ({ ...p, [status]: !p[status] }))}
                            className="w-full flex items-center justify-between p-2.5 rounded-lg border border-border bg-muted/30 text-left hover:bg-muted/50 transition-colors"
                            data-testid={`gate-group-${status}`}
                          >
                            <span className="text-xs font-medium text-muted-foreground flex items-center gap-2">
                              {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                              {label} ({group.length})
                            </span>
                            <span className="text-[10px] text-muted-foreground">{group.filter(canHumanSelect).length} available for human selection</span>
                          </button>
                          {open && (
                            <div className="mt-1.5 space-y-1.5 pl-2">
                              {group.map(rule => (
                                <div key={reviewIdentity(rule)}
                                  className="p-3 rounded-lg border border-border/60 bg-muted/20"
                                  data-testid={`gated-rule-${reviewIdentity(rule)}`}>
                                  <label className="flex items-center gap-2 mb-2 text-xs">
                                    <input type="checkbox" checked={!!rule.selected} disabled={!canHumanSelect(rule)}
                                      onChange={() => toggleRule(reviewIdentity(rule))}
                                      data-testid={`review-select-${reviewIdentity(rule)}`} />
                                    {canHumanSelect(rule) ? 'Select requirement' : 'Blocked'}
                                  </label>
                                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                    <Badge variant="outline" className="text-[10px] px-1.5">{rule.category}</Badge>
                                    <Badge className="text-[10px] px-1.5 bg-amber-500/10 text-amber-700 border border-amber-500/30">
                                      {label}
                                    </Badge>
                                    <Badge className={`text-[10px] px-1.5 border ${SEVERITY_COLORS[rule.severity] || SEVERITY_COLORS.major}`}>
                                      {rule.severity}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-foreground">{rule.description}</p>
                                  <ReviewRuntimeStatus rule={rule} onScopeChange={setReviewScope} />
                                  {(rule.display_gate_reason || rule.gate_reason || rule.verifier_reason) && (
                                    <p className="text-[11px] text-muted-foreground mt-0.5"
                                       data-testid={`gate-reason-${reviewIdentity(rule)}`}>
                                      <span className="font-medium">Reason:</span> {rule.display_gate_reason || rule.gate_reason || rule.verifier_reason}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* P02 Batch 2 (F3) — blocked outcomes from the last confirm call */}
                  {confirmBlocked.length > 0 && (
                    <div className="mt-3 p-3 rounded-lg border border-destructive/40 bg-destructive/5"
                         data-testid="confirm-blocked-banner">
                      <p className="text-xs font-semibold text-destructive mb-1">
                        {confirmBlocked.length} rule(s) were blocked by the promotion guard:
                      </p>
                      {confirmBlocked.map(b => (
                        <p key={b.candidate_pk || b.rule_id} className="text-[11px] text-muted-foreground"
                           data-testid={`blocked-rule-${b.candidate_pk || b.rule_id}`}>
                          <span className="font-mono text-foreground">{b.review_ref || b.rule_id}</span> — {b.reason}
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Confirm button */}
                  <Button onClick={confirmRules} disabled={confirming || humanRules.filter(r => r.selected).length === 0}
                    className="mt-4 w-full" data-testid="confirm-rules-btn">
                    {confirming ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</> :
                      <><Check className="mr-2 h-4 w-4" /> Save {humanRules.filter(r => r.selected).length} Selected Requirements</>}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
          )
        )}

        {/* ── STEP 2: IFC Validation ── */}
        {step === 2 && (
          <div className="space-y-6">
            <RuleSaveConfirmation summary={ruleSaveSummary} />
            {/* Validation Profile — pick which checks to run */}
            {!isReadOnly && <Card className="card-elevated" data-testid="validation-profile-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <ListChecks className="h-4 w-4 text-accent" />
                  Validation Profile
                </CardTitle>
                <CardDescription className="text-xs">
                  Select which checks to run. Disabled checks are skipped and don't affect the score.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {VALIDATION_PROFILE_OPTIONS.map(opt => {
                    const tone = VALIDATION_TONE_CLASSES[opt.tone] || VALIDATION_TONE_CLASSES.sky;
                    const active = !!validationProfile[opt.key];
                    const isBepRules = opt.key === 'custom_bep_rules';
                    const description = isBepRules
                      ? `${opt.description} (${rulesCounts.active} active rules)`
                      : opt.description;
                    return (
                      <label key={opt.key}
                        className={`flex items-start gap-2.5 p-2.5 rounded-md border cursor-pointer transition-colors ${
                          isBepRules ? 'sm:col-span-3' : ''
                        } ${
                          active ? tone.active : tone.inactive
                        }`}
                        data-testid={`profile-option-${opt.key}`}>
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() => toggleProfileOption(opt.key)}
                          className={`mt-0.5 cursor-pointer ${tone.accent}`}
                          data-testid={`profile-checkbox-${opt.key}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">{opt.label}</p>
                          <p className="text-[11px] text-muted-foreground">{description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <p className="mt-3 text-[11px] text-muted-foreground">
                  Active: <strong className="text-foreground">
                    {Object.values(validationProfile).filter(Boolean).length}
                  </strong> of {Object.keys(validationProfile).length} checks.
                  Default profile = custom BEP rules.
                </p>
              </CardContent>
            </Card>}

            {/* Active Validation Rules — review & customize before running IFC */}
            <Card className="card-elevated" data-testid="confirmed-rules-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <ListChecks className="h-4 w-4 text-accent" />
                      Active Validation Rules
                      <Badge className="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border border-[hsl(var(--success))]/30">
                        {rulesCounts.active} active
                      </Badge>
                      {rulesCounts.inactive > 0 && (
                        <Badge variant="outline" className="text-muted-foreground">
                          {rulesCounts.inactive} disabled
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {isReadOnly
                        ? 'Confirmed rules used by the demo audits. Changes are owner-only.'
                        : 'These rules will run against your IFC. Toggle, search, edit, or add manual rules before validation.'}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setRulesExpanded(!rulesExpanded)}
                      data-testid="toggle-rules-panel">
                      {rulesExpanded ? 'Hide' : 'Review Rules'}
                    </Button>
                    {!isReadOnly && unconfirmedCount > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setUnconfirmedModalOpen(true)}
                        data-testid="open-unconfirmed-candidates-btn"
                        className="border-accent/40 text-accent hover:bg-accent/10"
                        title="Add rules that were extracted but not yet activated — no BEP re-extraction needed."
                      >
                        + {unconfirmedCount} available rules
                      </Button>
                    )}
                    {!isReadOnly && <Button size="sm" variant="outline" onClick={() => { setStep(1); setCandidateRules([]); }}
                      data-testid="re-extract-bep-btn">
                      <FileText className="h-3.5 w-3.5 mr-1" /> Re-extract BEP
                    </Button>}
                  </div>
                </div>
              </CardHeader>
              {rulesExpanded && (
                <CardContent>
                  {rulesLoading ? (
                    <div className="flex justify-center py-6">
                      <Loader2 className="h-5 w-5 animate-spin text-accent" />
                    </div>
                  ) : confirmedRules.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">
                      No confirmed rules yet. Re-extract from the BEP or add a manual rule below.
                    </p>
                  ) : (
                    <>
                      {/* Controls */}
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <input
                          type="text"
                          placeholder="Search rules by description, field, category..."
                          value={ruleSearch}
                          onChange={(e) => setRuleSearch(e.target.value)}
                          className="flex-1 min-w-[200px] h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                          data-testid="rules-search"
                        />
                        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                          <input
                            type="checkbox"
                            checked={showInactive}
                            onChange={(e) => setShowInactive(e.target.checked)}
                            className="cursor-pointer"
                            data-testid="show-inactive-toggle"
                          />
                          <span className="text-muted-foreground">Show disabled</span>
                        </label>
                      </div>

                      {/* Rule list */}
                      <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-1">
                        {confirmedRules
                          .filter(r => showInactive || r.is_active)
                          .filter(r => {
                            if (!ruleSearch.trim()) return true;
                            const q = ruleSearch.toLowerCase();
                            return (r.description || '').toLowerCase().includes(q) ||
                                   (r.field_to_check || '').toLowerCase().includes(q) ||
                                   (r.category || '').toLowerCase().includes(q);
                          })
                          .map((rule, idx) => (
                            <div key={rule.id}
                              className={`flex items-start gap-3 p-3 rounded-lg border transition-colors
                                ${rule.is_active ? 'bg-accent/5 border-accent/30' : 'bg-muted/20 border-transparent opacity-60'}`}
                              data-testid={`confirmed-rule-${rule.id}`}>
                              {isReadOnly ? (
                                <span className="mt-0.5 shrink-0" data-testid={`rule-status-${rule.id}`}>
                                  {rule.is_active ?
                                    <SquareCheck className="h-4 w-4 text-accent" /> :
                                    <Square className="h-4 w-4 text-muted-foreground" />}
                                </span>
                              ) : <button onClick={() => toggleConfirmedRule(rule)}
                                className="mt-0.5 shrink-0 cursor-pointer"
                                aria-label={rule.is_active ? 'Disable rule' : 'Enable rule'}
                                data-testid={`toggle-rule-${rule.id}`}>
                                {rule.is_active ?
                                  <SquareCheck className="h-4 w-4 text-accent" /> :
                                  <Square className="h-4 w-4 text-muted-foreground" />}
                              </button>}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                  {/* 1-based numeric index — matches the visible order after
                                      the current search / show-disabled filters are applied,
                                      so users can call out "rule #7" and see the same row. */}
                                  <span
                                    className="inline-flex items-center justify-center min-w-[1.75rem] h-5 px-1.5 rounded-md text-[10px] font-semibold tabular-nums bg-muted text-muted-foreground"
                                    data-testid={`confirmed-rule-index-${idx + 1}`}
                                    title={`Rule #${idx + 1}`}
                                  >
                                    #{idx + 1}
                                  </span>
                                  <Badge variant="outline" className="text-[10px] px-1.5">{rule.category || 'GENERAL'}</Badge>
                                  <Badge className={`text-[10px] px-1.5 border ${SEVERITY_COLORS[rule.severity] || SEVERITY_COLORS.major}`}>
                                    {rule.severity}
                                  </Badge>
                                  {rule.check_type === 'manual' && (
                                    <Badge className="text-[10px] px-1.5 bg-accent/10 text-accent border border-accent/30">manual</Badge>
                                  )}
                                  {rule.check_type === 'deterministic' && (
                                    <Badge className="text-[10px] px-1.5 bg-accent/10 text-accent border border-accent/30">manifest</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-foreground">{rule.description}</p>
                                {(rule.field_to_check || rule.expected_pattern) && (
                                  <div className="flex flex-wrap gap-x-4 mt-1">
                                    {rule.field_to_check && (
                                      <p className="text-[11px] text-muted-foreground">Field: <span className="font-mono text-foreground">{rule.field_to_check}</span></p>
                                    )}
                                    {rule.expected_pattern && (
                                      <p className="text-[11px] text-muted-foreground">Expected: <span className="text-foreground">{rule.expected_pattern.length > 60 ? rule.expected_pattern.slice(0, 60) + '…' : rule.expected_pattern}</span></p>
                                    )}
                                  </div>
                                )}
                              </div>
                              {!isReadOnly && <button onClick={() => deleteConfirmedRule(rule)}
                                className="p-1.5 hover:bg-destructive/10 rounded text-muted-foreground hover:text-destructive shrink-0"
                                aria-label="Delete rule"
                                data-testid={`delete-rule-${rule.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>}
                            </div>
                          ))}
                      </div>

                      {/* Add manual rule */}
                      {!isReadOnly && <div className="mt-4 pt-4 border-t border-border">
                        <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                          <Plus className="h-3.5 w-3.5" /> Add manual rule
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-[1fr,140px,140px,auto] gap-2">
                          <input
                            type="text"
                            placeholder="Rule description (e.g. 'All MEP elements must have Manufacturer property')"
                            value={newRuleDesc}
                            onChange={(e) => setNewRuleDesc(e.target.value)}
                            className="h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                            data-testid="new-rule-desc"
                          />
                          <select value={newRuleCategory} onChange={(e) => setNewRuleCategory(e.target.value)}
                            className="h-9 px-3 rounded-md border border-input bg-background text-sm">
                            <option value="GENERAL">General</option>
                            <option value="NAMING">Naming</option>
                            <option value="FM_PARAMETERS">FM Params</option>
                            <option value="METADATA">Metadata</option>
                            <option value="DISCIPLINE">Discipline</option>
                          </select>
                          <select value={newRuleSeverity} onChange={(e) => setNewRuleSeverity(e.target.value)}
                            className="h-9 px-3 rounded-md border border-input bg-background text-sm">
                            <option value="critical">Critical</option>
                            <option value="major">Major</option>
                            <option value="minor">Minor</option>
                          </select>
                          <Button size="sm" onClick={addManualRule}
                            disabled={addingRule || !newRuleDesc.trim()}
                            data-testid="add-rule-btn">
                            {addingRule ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                          </Button>
                        </div>
                      </div>}
                    </>
                  )}
                </CardContent>
              )}
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Upload + Run */}
            {!isReadOnly && <Card className="card-elevated lg:col-span-1">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Step 2: Validate IFC</CardTitle>
                <CardDescription className="text-xs">Upload IFC and O&M files to run the Quality Gate</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <MiniDropZone label="IFC Model (required)" icon={FileCode}
                  accept={{ 'application/x-step': ['.ifc'], 'application/ifc': ['.ifc'] }}
                  files={ifcFiles}
                  onDrop={(accepted) => { chunkedUploadIdRef.current = null; setSelectedPreviousUpload(null); setIfcFiles(accepted.map(makeFileObj)); }}
                  onRemove={() => { chunkedUploadIdRef.current = null; setIfcFiles([]); }}
                  testId="step2-ifc-drop"
                  showPreviousPicker
                  previousUploads={previousIfcUploads}
                  selectedPrevious={selectedPreviousUpload}
                  onSelectPrevious={handleSelectPreviousUpload} />
                <MiniDropZone label="O&M Manuals (optional)" icon={BookOpen}
                  accept={{ 'application/pdf': ['.pdf'] }} multiple
                  files={omFiles}
                  onDrop={(accepted) => setOmFiles(prev => [...prev, ...accepted.map(makeFileObj)])}
                  onRemove={(id) => setOmFiles(omFiles.filter(f => f.id !== id))}
                  testId="step2-om-drop" />

                {uploadStep && <p className="text-xs text-accent">{uploadStep}</p>}

                {namingMismatch && (
                  <div className="border border-amber-500/50 bg-amber-500/10 rounded-md p-4 space-y-2"
                    data-testid="naming-mismatch-dialog">
                    <p className="text-sm font-semibold text-amber-700">This file may not belong to this project</p>
                    <ul className="text-xs text-amber-800 space-y-0.5">
                      {namingMismatch.errors.map((e, i) => <li key={i}>• {e}</li>)}
                    </ul>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" variant="outline" onClick={() => setNamingMismatch(null)}
                        data-testid="naming-mismatch-cancel-btn">
                        Cancel
                      </Button>
                      <Button size="sm" variant="outline"
                        className="border-amber-500/60 text-amber-700 hover:bg-amber-500/20"
                        onClick={() => {
                          if (namingMismatch.source_audit_id) {
                            runFromPreviousUpload(namingMismatch.source_audit_id, { confirmMismatch: true, force: true });
                          } else if (canUseLeanRerun(namingMismatch.upload_ref, namingMismatch.source)) {
                            rerunFromUploadRef(namingMismatch.upload_ref, { confirmMismatch: true, force: true });
                          } else if (canUseLeanChunkedRerun(namingMismatch.source)) {
                            rerunFromChunkedUploadId(chunkedUploadIdRef.current, { confirmMismatch: true, force: true });
                          } else {
                            runAudit({ confirmMismatch: true, force: true });
                          }
                        }}
                        data-testid="naming-mismatch-validate-anyway-btn">
                        Validate anyway
                      </Button>
                    </div>
                  </div>
                )}
                {duplicateAuditInfo && (
                  <Alert className="border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5" data-testid="duplicate-audit-alert">
                    <AlertCircle className="h-4 w-4 text-[hsl(var(--accent))]" />
                    <AlertDescription className="space-y-2">
                      <p className="text-sm">
                        This IFC was already validated on{' '}
                        <strong>{duplicateAuditInfo.created_at ? new Date(duplicateAuditInfo.created_at).toLocaleString() : 'unknown'}</strong>{' '}
                        with the <strong>same validation profile</strong>. Validated{' '}
                        {duplicateAuditInfo.validated_elements} of {duplicateAuditInfo.total_elements} elements.
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Change any profile checkbox above to get a fresh audit automatically, or click "Run anyway" to re-run with the same profile.
                      </p>
                      <div className="flex flex-wrap gap-2 pt-1">
                        <Button size="sm" variant="outline"
                          onClick={() => navigate(`/results/${duplicateAuditInfo.audit_id}`)}
                          data-testid="view-existing-audit-btn">
                          View existing results
                        </Button>
                        <Button size="sm"
                          onClick={() => {
                            if (duplicateAuditInfo.source_audit_id) {
                              runFromPreviousUpload(duplicateAuditInfo.source_audit_id, { force: true, confirmMismatch: true });
                            } else if (canUseLeanRerun(duplicateAuditInfo.upload_ref, duplicateAuditInfo.source)) {
                              rerunFromUploadRef(duplicateAuditInfo.upload_ref, { force: true, confirmMismatch: true });
                            } else if (canUseLeanChunkedRerun(duplicateAuditInfo.source)) {
                              rerunFromChunkedUploadId(chunkedUploadIdRef.current, { force: true, confirmMismatch: true });
                            } else {
                              runAudit({ force: true, confirmMismatch: true });
                            }
                          }}
                          disabled={uploading}
                          className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white"
                          data-testid="run-anyway-btn">
                          {uploading ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
                          Run anyway
                        </Button>
                        <Button size="sm" variant="ghost"
                          onClick={() => setDuplicateAuditInfo(null)}
                          data-testid="dismiss-duplicate-btn">
                          Dismiss
                        </Button>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                <Button onClick={() => selectedPreviousUpload ? runFromPreviousUpload(selectedPreviousUpload.audit_id) : runAudit()}
                  disabled={uploading || (ifcFiles.length === 0 && !selectedPreviousUpload) || rulesCounts.active === 0}
                  className="w-full" data-testid="run-quality-gate">
                  {uploading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Running...</> :
                    <><CheckCircle2 className="mr-2 h-4 w-4" /> Run Quality Gate</>}
                </Button>
                {rulesCounts.active === 0 && (
                  <p className="text-xs text-[hsl(var(--warning))]">Enable at least one rule above before running validation.</p>
                )}
              </CardContent>
            </Card>}

            {/* Recent IFC Validations */}
            <Card className={`card-elevated ${isReadOnly ? 'lg:col-span-3' : 'lg:col-span-2'}`}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Recent IFC Validations</CardTitle>
                <div className="flex items-center gap-3 mt-1.5">
                  <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span className="inline-block h-2.5 w-2.5 rounded-full bg-[hsl(var(--destructive))]" />
                    Failed
                  </span>
                  <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span className="inline-block h-2.5 w-2.5 rounded-full bg-[hsl(var(--warning))]" />
                    Needs Review
                  </span>
                  <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span className="inline-block h-2.5 w-2.5 rounded-full bg-[hsl(var(--success))]" />
                    Passed
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-accent" /></div>
                ) : audits.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No IFC validations yet. Upload an IFC to run your first one.</p>
                ) : (
                  <div className="space-y-2">
                    {audits.slice(0, 5).map((audit) => {
                      const summary = audit.summary || {};
                      const elapsedDuration = formatDuration(audit.created_at, audit.completed_at);
                      const isFailed = audit.status === 'failed';
                      // Compliance verdict for this audit's elements. fail_count/review_count/
                      // pass_count already include the project's own custom BEP rule results
                      // (unlike self_certified/hitl_flagged, which only reflect built-in
                      // naming/discipline/FM/metadata/spatial checks and should NOT be used
                      // for this).
                      const failCount = summary.fail_count || 0;
                      const reviewCount = summary.review_count || 0;
                      const passCount = summary.pass_count || 0;
                      const hasVerdict = audit.status === 'completed' && (failCount + reviewCount + passCount) > 0;
                      const verdict = !hasVerdict
                        ? null
                        : failCount > 0 ? 'failed'
                        : reviewCount > 0 ? 'review'
                        : 'passed';
                      const verdictMeta = {
                        failed: { color: 'bg-[hsl(var(--destructive))]', label: 'Failed' },
                        review: { color: 'bg-[hsl(var(--warning))]', label: 'Needs Review' },
                        passed: { color: 'bg-[hsl(var(--success))]', label: 'Passed' },
                      };
                      return (
                        <div key={audit.id}
                          className={`p-3 rounded-md transition-colors ${audit.status === 'completed' ? 'hover:bg-muted/50 cursor-pointer' : 'bg-[hsl(var(--destructive))]/5'}`}
                          onClick={() => audit.status === 'completed' && navigate(`/results/${audit.id}`)}
                          data-testid={`audit-row-${audit.id}`}>
                          <div className="flex items-center justify-between gap-3 min-w-0">
                            <div className="flex items-center gap-3 min-w-0">
                            {isFailed ? (
                              <XCircle className="h-4 w-4 text-destructive shrink-0" />
                            ) : (
                              <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))] shrink-0" />
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate max-w-xs" data-testid={`audit-filename-${audit.id}`}>{audit.ifc_file_name}</p>
                              {audit.naming_mismatch_ack && (
                                <span title={audit.ifc_file_name}
                                  className="inline-block text-[10px] px-1.5 py-0.5 mt-0.5 rounded border border-amber-500/50 bg-amber-500/10 text-amber-700"
                                  data-testid={`naming-mismatch-badge-${audit.id}`}>
                                  Naming mismatch — validated on user confirmation
                                </span>
                              )}
                              <p className="text-xs text-muted-foreground" data-testid={`audit-metadata-${audit.id}`}>
                                {formatDateTime(audit.created_at)}
                                {audit.total_elements != null && ` · ${audit.total_elements.toLocaleString()} total elements`}
                                {audit.validated_elements != null && audit.total_elements != null && ` · Validated: ${audit.validated_elements.toLocaleString()} of ${audit.total_elements.toLocaleString()}`}
                                {audit.validation_coverage_percent != null && ` (${Number(audit.validation_coverage_percent).toFixed(0)}%)`}
                                {audit.audit_duration_seconds != null && ` · Processing duration: ${audit.audit_duration_seconds}s`}
                                {elapsedDuration && ` · Elapsed: ${elapsedDuration}`}
                                {verdict ? ` · Validation outcome: ${verdictMeta[verdict].label}` : ''}
                              </p>
                            </div>
                            </div>
                            {isFailed && (
                              <span className="text-xs font-semibold text-destructive shrink-0" data-testid={`audit-status-${audit.id}`}>
                                Failed
                              </span>
                            )}
                          </div>
                          {isFailed && (
                            <p className="mt-2 pl-7 text-xs text-destructive" data-testid={`audit-failure-reason-${audit.id}`}>
                              {audit.error_message || audit.failure_reason || 'Audit failed before finalization.'}
                            </p>
                          )}
                          {verdict && (
                            <span
                              className={`inline-block h-4 w-4 rounded-full shrink-0 ${verdictMeta[verdict].color}`}
                              title={verdictMeta[verdict].label}
                              data-testid={`audit-verdict-${audit.id}`}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
            </div>
          </div>
        )}
      </div>
      {!isReadOnly && <UnconfirmedCandidatesModal
        open={unconfirmedModalOpen}
        onOpenChange={setUnconfirmedModalOpen}
        projectId={projectId}
        onActivated={() => {
          // Refresh active rules + the unconfirmed count so the
          // button updates/hides without a page reload.
          fetchConfirmedRules();
        }}
      />}
    </DashboardLayout>
  );
}
